import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCloudArrowUp,
  faFileImage,
  faFilePdf,
  faTrash,
  faGlobe,
} from "@fortawesome/pro-light-svg-icons";
import { faSpinner } from "@fortawesome/pro-regular-svg-icons";
import Modal from "../../../utils/Modal";
import Button from "../../../buttons/Button";
import Alert from "../../../utils/Alert";
import {
  startMenuImport,
  startMenuImportFromUrl,
  getRemainingImports,
} from "../../../../services/aiMenuService";
import { toast } from "../../../../utils/demoToast";

const ACCEPTED_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "application/pdf",
];
const MAX_FILES = 20;

/**
 * Modal component for AI-powered menu import.
 * Fire-and-forget: submits the import request and closes immediately.
 * The user is notified via WebSocket / toast when the import finishes.
 */
const ImportMenuModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const restaurantId = useSelector((state) => state.user.restaurantId);
  const [mode, setMode] = useState("files"); // "files" | "url"
  const [files, setFiles] = useState([]);
  const [urlInput, setUrlInput] = useState("");
  const [quota, setQuota] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  // Fetch remaining import quota and active-import status when modal opens
  useEffect(() => {
    if (isOpen && restaurantId) {
      getRemainingImports(restaurantId)
        .then((res) => setQuota(res.data))
        .catch(() => setQuota(null));
    }
    if (isOpen) {
      setMode("files");
      setFiles([]);
      setUrlInput("");
      setSubmitting(false);
    }
  }, [isOpen, restaurantId]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const addFiles = useCallback(
    (newFiles) => {
      const combined = [...files, ...newFiles].slice(0, MAX_FILES);
      setFiles(combined);
    },
    [files],
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const droppedFiles = Array.from(e.dataTransfer.files).filter((f) =>
        ACCEPTED_TYPES.includes(f.type),
      );
      addFiles(droppedFiles);
    },
    [addFiles],
  );

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files).filter((f) =>
      ACCEPTED_TYPES.includes(f.type),
    );
    addFiles(selected);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleImport = async () => {
    if (mode === "files" && files.length === 0) {
      toast.error(t("dashboard.products.import.noFiles"));
      return;
    }
    if (mode === "url" && !urlInput.trim()) {
      toast.error(t("dashboard.products.import.noUrl"));
      return;
    }

    setSubmitting(true);

    try {
      const res =
        mode === "files"
          ? await startMenuImport(restaurantId, files)
          : await startMenuImportFromUrl(restaurantId, urlInput.trim());

      // Fire-and-forget: show toast and close modal
      if (res.data.async === false && res.data.result) {
        // Sync mode (local dev without Redis): result came back immediately
        toast.success(
          t("dashboard.products.import.completedToast", {
            count: res.data.result.dishesCreated,
          }),
        );
      } else {
        toast.info(t("dashboard.products.import.submittedToast"));
      }

      onClose();
    } catch (err) {
      setSubmitting(false);
      const msg =
        err?.response?.data?.message || err.message || "Import failed";
      toast.error(msg);
    }
  };

  const getFileIcon = (file) => {
    if (file.type === "application/pdf") return faFilePdf;
    return faFileImage;
  };

  const isQuotaExhausted =
    quota && quota.remaining !== -1 && quota.remaining <= 0;

  const hasActiveImport = quota && quota.activeImport !== null;

  const isImportDisabled =
    submitting ||
    hasActiveImport ||
    (mode === "files" ? files.length === 0 : !urlInput.trim());

  return (
    <Modal id="import-menu-modal" isOpen={isOpen} onClose={onClose} noFooter>
      <div className="p-4" data-tour="import-menu-modal">
        <h2 className="mt-0">{t("dashboard.products.import.title")}</h2>

        {/* Active import warning */}
        {hasActiveImport && !isQuotaExhausted && (
          <Alert variant="pastel-warning" className="mb-3">
            <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
            <span>{t("dashboard.products.import.activeImportWarning")}</span>
          </Alert>
        )}

        {/* Quota info */}
        {quota && !isQuotaExhausted && !hasActiveImport && (
          <Alert variant="pastel-info" className="mb-3">
            {quota.remaining === -1 ? (
              <span>{t("dashboard.products.import.unlimitedQuota")}</span>
            ) : (
              <span>
                {t("dashboard.products.import.remainingQuota", {
                  remaining: quota.remaining,
                  limit: quota.limit,
                })}
              </span>
            )}
          </Alert>
        )}

        {/* Quota exhausted message */}
        {isQuotaExhausted && (
          <div className="text-center py-4">
            <p className="text-light p2 italic">
              {t("dashboard.products.import.quotaExhausted")}
            </p>
            <Button variant="primary" onClick={onClose} className="mt-3">
              {t("utils.common.close")}
            </Button>
          </div>
        )}

        {/* Input area — visible when not exhausted and not submitting */}
        {!isQuotaExhausted && !submitting && (
          <>
            {/* Mode toggle */}
            <div className="import-mode-toggle">
              <div className="import-mode-toggle__pills">
                <button
                  type="button"
                  className={`import-mode-toggle__btn ${mode === "files" ? "import-mode-toggle__btn--active" : ""}`}
                  onClick={() => setMode("files")}
                >
                  <FontAwesomeIcon icon={faCloudArrowUp} />
                  {t("dashboard.products.import.modeFiles")}
                </button>
                <button
                  type="button"
                  className={`import-mode-toggle__btn ${mode === "url" ? "import-mode-toggle__btn--active" : ""}`}
                  onClick={() => setMode("url")}
                >
                  <FontAwesomeIcon icon={faGlobe} />
                  {t("dashboard.products.import.modeUrl")}
                </button>
              </div>
            </div>

            {mode === "files" ? (
              <>
                {/* Dropzone */}
                <div
                  className="import-dropzone"
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FontAwesomeIcon
                    icon={faCloudArrowUp}
                    size="3x"
                    className="text-primary mb-2"
                  />
                  <p className="text-primary fw-bold">
                    {t("dashboard.products.import.dropzoneTitle")}
                  </p>
                  <p className="text-light text-sm">
                    {t("dashboard.products.import.dropzoneHint")}
                  </p>
                  <p className="text-light p4 mt-1 italic">
                    {t("dashboard.products.import.qualityHint")}
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".jpg,.jpeg,.png,.webp,.pdf"
                    onChange={handleFileSelect}
                    style={{ display: "none" }}
                  />
                </div>

                {/* File list */}
                {files.length > 0 && (
                  <div className="mt-3">
                    <p className="fw-bold mb-2">
                      {t("dashboard.products.import.selectedFiles", {
                        count: files.length,
                      })}
                    </p>
                    <div className="import-file-list">
                      {files.map((file, index) => (
                        <div key={index} className="import-file-item">
                          <FontAwesomeIcon
                            icon={getFileIcon(file)}
                            className="mr-2"
                          />
                          <span className="import-file-name">{file.name}</span>
                          <span className="text-light text-sm ml-auto mr-2">
                            {(file.size / 1024 / 1024).toFixed(1)} MB
                          </span>
                          <button
                            className="import-file-remove"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFile(index);
                            }}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* URL input area */}
                <div className="import-url-input">
                  <FontAwesomeIcon
                    icon={faGlobe}
                    size="3x"
                    className="text-primary mb-2"
                  />
                  <p className="text-primary fw-bold">
                    {t("dashboard.products.import.urlTitle")}
                  </p>
                  <p className="text-light text-sm mb-3">
                    {t("dashboard.products.import.urlHint")}
                  </p>
                  <input
                    type="url"
                    className="import-url-input__field"
                    placeholder="https://restaurant.ch/menu"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                  />
                </div>
              </>
            )}

            {/* Actions */}
            <div className="flex gap-1 mt-4 justify-end">
              <Button variant="primary" onClick={onClose}>
                {t("utils.common.close")}
              </Button>
              <Button
                variant="success"
                onClick={handleImport}
                disabled={isImportDisabled}
              >
                {t("dashboard.products.import.startImport")}
              </Button>
            </div>
          </>
        )}

        {/* Brief submitting indicator (only visible for the short API call) */}
        {submitting && (
          <div className="text-center py-4">
            <FontAwesomeIcon
              icon={faSpinner}
              spin
              size="2x"
              className="text-primary mb-3"
            />
            <p className="text-light">
              {t("dashboard.products.import.processing")}
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ImportMenuModal;
